import React, { useMemo, useState } from "react";
import { FileUpload } from "@components/FileUpload/FileUpload";
import { useFileUploadProfiles } from "@components/FileUpload/FileUpload.constants";
import SingleFile from "@components/FileUpload/SingleFile";
import type { UploadEntry } from "@components/FileUpload/uploadStore";
import { useNotification } from "@context/NotificationContext";
import {
  drybulkCargoOperationsService,
  type Attachment,
  type StagedAttachment,
} from "@services/drybulkCargoOperationsApi";
import { useTranslation } from "react-i18next";
import { DRYBULK_CARGO_OPS_LANG_BASE, type PlanAttachmentType } from "../constants";
import { useDownloadCargoOperationAttachment } from "../hooks/useDownloadCargoOperationAttachment";
import { getAttachmentFileName } from "../utils/attachmentFileName";

const L = `${DRYBULK_CARGO_OPS_LANG_BASE}`;

interface PlanAttachmentSlotProps {
  attachmentType: PlanAttachmentType;
  /** The file already in Blob and waiting for Save as draft / Add Operation. */
  staged: StagedAttachment | null;
  onStagedChange: (staged: StagedAttachment | null) => void;
  /** The plan the operation already holds: shown read-only, and the drop zone is disabled. */
  attached: Attachment | null;
  disabled?: boolean;
}

/** `FileUpload` has no `name`, so nothing reaches the localStorage-backed upload store (the Add page's id changes on every visit). */
const PlanAttachmentSlot: React.FC<PlanAttachmentSlotProps> = ({
  attachmentType,
  staged,
  onStagedChange,
  attached,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const { notify } = useNotification();
  const profiles = useFileUploadProfiles();
  const download = useDownloadCargoOperationAttachment();
  const conditions = useMemo(
    () => ({ ...profiles.drybulkCargoOpsPlan, hideErrors: false }),
    [profiles.drybulkCargoOpsPlan],
  );
  const allowedExtensions = useMemo(
    () => conditions.format.map((ext) => `.${ext}`),
    [conditions.format],
  );
  const [files, setFiles] = useState<UploadEntry[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const testId = `drybulk-plan-slot-${attachmentType}`;

  const handleUpload = async (selected: File[]) => {
    const file = selected[selected.length - 1];
    if (!file) return;
    setIsUploading(true);
    try {
      onStagedChange(await drybulkCargoOperationsService.stageAttachment(file, attachmentType));
    } catch {
      setFiles([]);
      notify({
        type: "error",
        title: t("common.error"),
        description: t(`${L}.documents.upload-failed`),
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemove = () => {
    setFiles([]);
    onStagedChange(null);
  };

  return (
    <div className="flex flex-col gap-2 py-2" data-testid={testId}>
      <h4 className="text-sm font-medium">
        {t(`${L}.add.${attachmentType === "LOADING_PLAN" ? "loading-plan" : "stowage-plan"}`)}
      </h4>
      <FileUpload
        testId={`${testId}-upload`}
        onUpload={(selected) => void handleUpload(selected)}
        files={staged ? [{ name: staged.originalFileName ?? staged.documentUrl }] : files}
        setFiles={setFiles}
        maxFiles={1}
        allowedExtensions={allowedExtensions}
        conditions={conditions}
        disabled={disabled || isUploading || attached !== null}
        uploadDescription={t("upload.additional-documents-upload", {
          extensions: conditions.format.join(", "),
          size: "10MB",
        })}
      />
      {attached ? (
        <SingleFile
          testId={`${testId}-attached`}
          file={{ name: getAttachmentFileName(attached) }}
          fieldName={testId}
          setFiles={() => undefined}
          disabled
          onView={() => void download.openAttachment(attached.documentUrl)}
        />
      ) : (
        staged && (
          <SingleFile
            testId={`${testId}-file`}
            file={{ name: staged.originalFileName ?? staged.documentUrl }}
            fieldName={testId}
            setFiles={() => undefined}
            removeFn={handleRemove}
            disabled={disabled}
          />
        )
      )}
    </div>
  );
};

export default PlanAttachmentSlot;
